import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Trechos marcados com * ficam em verde quando acendem.
const TEXT =
  'A Idealizer nasceu em 2023 com uma convicção: tecnologia boa é a que *chega nas mãos das pessoas.* Por isso cuidamos de tudo, *do design ao backend,* até o seu produto estar *no ar e crescendo.*';

const FACTS = [
  { value: '4', label: 'produtos publicados e em uso' },
  { value: '3', label: 'plataformas: iOS, Android e web' },
  { value: '2023', label: 'o ano em que começamos' },
  { value: '1', label: 'time do design ao backend' },
];

function tokens(text) {
  let accent = false;
  return text.split(' ').map((raw, i) => {
    let w = raw;
    const opens = w.startsWith('*');
    if (opens) {
      accent = true;
      w = w.slice(1);
    }
    const closes = w.endsWith('*');
    w = w.replace('*', '');
    const t = { w, accent, key: `${i}-${w}` };
    if (closes) accent = false;
    return t;
  });
}

/** Dígitos em coluna (0 a 9) que rolam até o valor, como um odômetro. */
function Odometer({ value }) {
  return (
    <span className="hv3-odo" aria-hidden>
      {value.split('').map((d, i) => (
        <span className="hv3-odo-col" key={i} data-digit={d}>
          <span className="hv3-odo-strip">
            {Array.from({ length: 10 }, (_, n) => (
              <span key={n}>{n}</span>
            ))}
          </span>
        </span>
      ))}
    </span>
  );
}

function Manifesto() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Cada palavra acende conforme a leitura avança com o scroll.
        gsap.fromTo(
          q('.hv3-mw'),
          { opacity: 0.14 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.12,
            scrollTrigger: { trigger: q('.hv3-manifesto-text'), start: 'top 78%', end: 'bottom 45%', scrub: 0.4 },
          }
        );

        // Odômetro: cada coluna gira até o dígito, com leve atraso entre colunas.
        q('.hv3-odo-col').forEach((col, i) => {
          const d = Number(col.dataset.digit);
          gsap.fromTo(
            col.firstChild,
            { yPercent: 0 },
            {
              yPercent: -d * 10,
              duration: 1.8 + i * 0.12,
              ease: 'expo.out',
              scrollTrigger: { trigger: q('.hv3-facts'), start: 'top 82%' },
            }
          );
        });

        gsap.from(q('.hv3-fact-label, .hv3-fact-rule'), {
          autoAlpha: 0,
          y: 14,
          duration: 1,
          ease: 'expo.out',
          stagger: 0.06,
          scrollTrigger: { trigger: q('.hv3-facts'), start: 'top 82%' },
        });
      });

      mm.add('(prefers-reduced-motion: reduce)', () => {
        q('.hv3-odo-col').forEach((col) => {
          gsap.set(col.firstChild, { yPercent: -Number(col.dataset.digit) * 10 });
        });
      });

      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section className="hv3-manifesto" ref={root} aria-label="Quem somos">
      <div className="hv3-wrap">
        <p className="hv3-manifesto-text">
          {tokens(TEXT).map((t) => (
            <span key={t.key} className={`hv3-mw ${t.accent ? 'is-accent' : ''}`}>
              {t.w}{' '}
            </span>
          ))}
        </p>

        <dl className="hv3-facts">
          {FACTS.map((f) => (
            <div className="hv3-fact" key={f.label}>
              <span className="hv3-fact-rule" aria-hidden />
              <dt className="hv3-fact-value">
                <span className="hv3-sr">{f.value}</span>
                <Odometer value={f.value} />
              </dt>
              <dd className="hv3-fact-label">{f.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export default Manifesto;
